import React from 'react';
import { Box, HStack, Icon, VStack } from '@chakra-ui/react';
import { LuTag } from 'react-icons/lu';
import { DocumentWithDownloadUrl as Document } from '@/client';
import CollapsibleCard from '@/components/common/CollapsibleCard';
import { useDocumentStatus } from '@/hooks/house/documents/useDocumentStatus';
import { getClassificationTag, isImportant } from '@/utils/documentUtils';
import OutlineBadge from '@/components/common/OutlineBadge';
import ProcessingBadge, { ProcessingBadgeKind } from '@/components/common/ProcessingBadge';
import OutputBadgeTransition from '@/components/common/OutputBadgeTransition';
import GeneratedByBundleNote from '@/components/common/GeneratedByBundleNote';

interface DocumentTagsProps {
	document: Document;
	/** A pending share isn't in this house's pipeline; render its tags as-is, never as "Processing". */
	isPendingShare?: boolean;
}

/**
 * Component for displaying document classification tags in a collapsible card
 * Shows tags in badge format with "Tags generated by Bundle" footer
 */
const DocumentTags: React.FC<DocumentTagsProps> = ({ document, isPendingShare = false }) => {
	const classificationTag = getClassificationTag(document);

	// The classification tag is produced by document_enrichment (same owner as the summary), so gate on
	// the enrichment path rather than the generic pipeline flag. A pending share's document isn't in this
	// house's status stream, so skip the lookup: its tags come from the share payload and must never read
	// as perpetually "Processing".
	const { isEnrichmentPending } = useDocumentStatus(isPendingShare ? null : document.id, document.uploaded_at);
	const isImportantRecord = isImportant(document);

	// Don't render if no classification tags, not important, and not processing
	if (!classificationTag && !isImportantRecord && !isEnrichmentPending) {
		return null;
	}

	const tagIcon = <Icon as={LuTag} boxSize={5} color="brand.fg" />;

	return (
		<CollapsibleCard title="Tags" icon={tagIcon} value="tags" autoScrollOnOpen={true}>
			<VStack align="stretch" gap={3}>
				{/* Tags display */}
				<HStack gap={2} wrap="wrap" pr={5} pb={!classificationTag && !isImportantRecord ? 2 : 0}>
					{isImportantRecord && (
						<OutlineBadge borderRadius="full" textStyle="label">
							Important
						</OutlineBadge>
					)}
					<OutputBadgeTransition
						pending={!classificationTag && !isImportantRecord && isEnrichmentPending}
						ready={!!classificationTag}
						pendingContent={<ProcessingBadge kind={ProcessingBadgeKind.Tags} />}
					>
						{classificationTag ? (
							<OutlineBadge borderRadius="full" textStyle="label">
								{classificationTag.name}
							</OutlineBadge>
						) : null}
					</OutputBadgeTransition>
				</HStack>

				{/* Footer text */}
				{(classificationTag || isImportantRecord) && (
					<Box pr={5}>
						<GeneratedByBundleNote label="Tags generated by Bundle" />
					</Box>
				)}
			</VStack>
		</CollapsibleCard>
	);
};

export default DocumentTags;
